// ============================================================================
// EditorTabs — la barre d'onglets des etapes de l'editeur de publication
// ============================================================================
// Composition LOCALE (UI_STANDARDS « composition locale legitime ») : @gs/ui
// n'a aucun onglet d'etapes portant un etat « pret / manquant » (GsSegmented ne
// prend que des libelles texte) — sa promotion est une question fermee du
// rapport, pas un ajout au paquet. La peau reprend celle de GsSegmented
// 'default' (piste slate, actif = pastille blanche, NEUTRE : jamais brand).
// Les onglets se partagent la largeur en colonnes egales : sur un telephone,
// tous restent atteignables sans defilement horizontal.
//
// A11y : patron WAI-ARIA « tabs » a activation automatique — role="tablist",
// onglets role="tab" aria-selected aria-controls, tabindex itinerant, fleches
// gauche/droite (en boucle), Debut, Fin. L'etat se lit en texte sous le
// libelle, jamais par la couleur seule.

import { useRef, type KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, CircleAlert } from 'lucide-react';
import { panelDomId, tabDomId } from './editorTabIds';

export interface EditorTab {
  id: string;
  label: string;
  icon: LucideIcon;
  ready: boolean;
  /** Pastille de compte (ex. nombre de pieces jointes), masquee si 0 ; decor, le panneau liste les fichiers. */
  count?: number;
}

/** Colonnes egales, classes statiques (Tailwind ne voit pas une classe calculee). */
const COLUMNS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };

export function EditorTabs({
  tabs,
  active,
  onChange,
  ariaLabel,
  readyLabel,
  missingLabel,
}: {
  tabs: EditorTab[];
  active: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  readyLabel: string;
  missingLabel: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function move(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = tabs.length - 1;
    const target =
      e.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
        : e.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
          : e.key === 'Home' ? 0
            : e.key === 'End' ? last
              : null;
    if (target === null) return;
    e.preventDefault();
    onChange(tabs[target].id);
    refs.current[target]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`grid gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800 ${COLUMNS[tabs.length] ?? 'grid-cols-3'}`}
    >
      {tabs.map((tab, i) => {
        const selected = tab.id === active;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            id={tabDomId(tab.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelDomId(tab.id)}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => move(e, i)}
            className={`flex min-w-0 touch-manipulation flex-col items-center gap-0.5 rounded-lg px-1.5 py-2 text-center transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
              selected
                ? 'bg-white shadow-xs dark:bg-slate-700'
                : 'hover:bg-slate-200/60 dark:hover:bg-slate-700/50'
            }`}
          >
            <span
              className={`flex items-center gap-1.5 text-field ${
                selected
                  ? 'font-semibold text-slate-800 dark:text-slate-100'
                  : 'font-medium text-slate-500 dark:text-slate-400'
              }`}
            >
              <Icon size={15} aria-hidden className="hidden shrink-0 sm:block" />
              <span className="min-w-0">{tab.label}</span>
              {tab.count ? (
                <span
                  aria-hidden
                  className="rounded-full bg-slate-200 px-1.5 text-meta font-semibold tabular-nums text-slate-600 dark:bg-slate-600 dark:text-slate-200"
                >
                  {tab.count}
                </span>
              ) : null}
            </span>
            {/* Espace entre libelle et etat dans le nom accessible (« Contenu Manquant »). */}
            {' '}
            <span
              className={`flex items-center gap-1 text-meta font-medium ${
                tab.ready ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
              }`}
            >
              {tab.ready ? <Check size={12} aria-hidden /> : <CircleAlert size={12} aria-hidden />}
              {tab.ready ? readyLabel : missingLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
}
